import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

/**
 * ProtectedRoute Component
 * 
 * Uses the shared useAuth hook to validate authentication and payment status.
 * This prevents duplicate API calls and leverages the global auth cache.
 * 
 * Flow:
 * 1. Use useAuth hook (which handles all auth logic with caching)
 * 2. If not authenticated: redirect to login
 * 3. If authenticated but no full access: redirect to subscription offers
 * 4. If full access: render children
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
    const { isAuthenticated, hasFullAccess, isLoading } = useAuth();
    const location = useLocation();


    // Show loading while validating
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Verificando autenticação...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Redirect to subscription offers if authenticated but no full access
    if (isAuthenticated && !hasFullAccess) {
        return <Navigate to="/subscription-offers" state={{ from: location }} replace />;
    }

    // Render protected content
    return <>{children}</>;
}
